import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Check } from "lucide-react";
import api from "@/lib/api";
import { formatINR } from "@/data/products";
import OrderTrackingTimeline from "@/components/OrderTrackingTimeline";

const STATUS_LABEL = {
  created: "Awaiting Payment",
  paid: "Paid",
  processing: "Processing",
  shipped: "Shipped",
  delivered: "Delivered",
  cancelled: "Cancelled",
  failed: "Failed",
};

const OrderConfirmation = () => {
  const { id } = useParams();
  const { data, isLoading } = useQuery({
    queryKey: ["order", id],
    queryFn: async () => (await api.get(`/orders/${id}`)).data,
  });

  if (isLoading) return null;
  const order = data?.order;
  if (!order) {
    return (
      <section className="min-h-[60vh] flex items-center justify-center">
        <p className="font-serif-lux italic font-light text-3xl text-[#5C5C5C]">Order not found.</p>
      </section>
    );
  }

  const paid = order.status === "paid" || order.status === "processing" || order.status === "shipped" || order.status === "delivered";

  return (
    <section className="min-h-[80vh] flex flex-col items-center justify-center px-6 py-32 text-center" data-testid="order-confirmation">
      <div className="w-16 h-16 rounded-full border border-[#C07C5F] flex items-center justify-center mb-8">
        <Check size={22} className="text-[#C07C5F]" />
      </div>
      <p className="font-serif-lux italic font-light text-4xl mb-4">{paid ? "Order Confirmed." : "Order Received."}</p>
      <p className="text-xs font-light text-[#5C5C5C] max-w-xs leading-relaxed mb-8">
        {paid
          ? "Thank you — your bars are on their way to being made."
          : "We're still waiting on payment confirmation. Refresh in a moment if this doesn't update."}
      </p>
      <p className="text-[10px] tracking-[0.35em] uppercase text-[#5C5C5C] mb-2">Order</p>
      <p className="font-serif-lux text-2xl tracking-wide" data-testid="order-number">{order.orderNumber}</p>
      <p className="text-[10px] tracking-[0.3em] uppercase text-[#5C5C5C] mt-4" data-testid="order-status">{STATUS_LABEL[order.status] || order.status}</p>
      <p className="font-serif-lux text-3xl mt-6">{formatINR(order.total)}</p>
      {order.pointsDiscount > 0 && (
        <p className="text-[10px] tracking-[0.3em] uppercase text-[#5C5C5C] mt-2">
          Includes {formatINR(order.pointsDiscount)} off with loyalty points
        </p>
      )}
      {order.pointsEarned > 0 && (
        <p className="text-[10px] tracking-[0.3em] uppercase text-[#C07C5F] mt-2" data-testid="order-points-earned">
          + {order.pointsEarned} loyalty points earned
        </p>
      )}

      {paid && <OrderTrackingTimeline order={order} queryKey={["order", id]} />}

      <Link
        to="/account"
        data-testid="order-continue-btn"
        className="mt-12 border border-[#111111] rounded-full px-8 py-3 text-[11px] tracking-[0.3em] uppercase hover:bg-[#111111] hover:text-[#F9F8F6] transition-colors duration-500"
      >
        View Your Orders
      </Link>
    </section>
  );
};

export default OrderConfirmation;
